'use client';

import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import { Form, FormField } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { copy } from '@/data/copy';
import {
  type RequestResetPasswordFormType,
  type SendNewPasswordFormType,
  sendNewPasswordSchema,
  requestResetPasswordSchema,
} from '@/schemas/resetPassword';
import { useToast } from '@/hooks/useToast';

import FormInput from '../formElements/FormInput';
import Mail from '../icons/Mail';
import InputPassword from '../formElements/InputPassword';
import InputSubmit from '../formElements/InputSubmit';
import VerifyEmailInput from '../formElements/VerifyEmailInput';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import {
  requestPasswordResetSDK,
  sendNewPasswordSDK,
} from '@/server/actions/userSDK';
import BackToPreviousPage from '../utils/BackToPreviousPage';

function BackToLoginComponent() {
  return (
    <p className="mt-4">
      {copy.pages.resetPassword.doNotWantToReset}{' '}
      <Link href="/signin" className="cta-link">
        {copy.pages.resetPassword.backToLogin}
      </Link>
    </p>
  );
}

export default function ResetPasswordForm() {
  const router = useRouter();
  const requestResetPasswordForm = useForm<RequestResetPasswordFormType>({
    resolver: zodResolver(requestResetPasswordSchema),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const { toast } = useToast();

  async function onRequestResetCodeSubmit(
    values: RequestResetPasswordFormType
  ) {
    setLoading(true);

    const { success, error } = await requestPasswordResetSDK(values);

    if (success && success.codeSentToUser) {
      toast({
        title: copy.pages.resetPassword.verificationCodeSent,
        description: copy.pages.resetPassword.verificationCodeSentDescription,
      });

      setCurrentUsername(values.username);
    }

    if (error) {
      setError(error);
    }

    setLoading(false);
  }

  const sendNewPasswordForm = useForm<SendNewPasswordFormType>({
    resolver: zodResolver(sendNewPasswordSchema),
  });

  async function onSendNewPasswordSubmit(values: SendNewPasswordFormType) {
    setLoading(true);

    const { success, error } = await sendNewPasswordSDK(values);

    if (success) {
      toast({
        title: copy.pages.resetPassword.passwordResetSuccess,
        description: copy.pages.resetPassword.passwordResetSuccessDescription,
      });

      setTimeout(() => {
        router.replace('/signin');
      }, 1500);

      return;
    }

    if (error) {
      setError(error);
    }

    setLoading(false);
  }

  return (
    <>
      {!currentUsername && (
        <Form {...requestResetPasswordForm}>
          <form
            onSubmit={requestResetPasswordForm.handleSubmit(
              onRequestResetCodeSubmit
            )}
            className="flex flex-col gap-8"
            method="POST"
          >
            {error && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription className="text-lg ml-2">
                  {error}
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setError(null);
                      setCurrentUsername(null);
                    }}
                  >
                    {copy.pages.resetPassword.tryAgain}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <BackToPreviousPage className="text-md" />

            <FormField
              control={requestResetPasswordForm.control}
              name="username"
              render={({ field }) => (
                <FormInput
                  label={copy.common.form.emailAddress}
                  placeholder={`${copy.common.form.your} ${copy.common.form.emailAddress}`}
                  icon={<Mail />}
                  autoComplete="username"
                  {...field}
                />
              )}
            />

            <InputSubmit
              hasIcon
              className="uppercase"
              isLoading={loading}
              disabled={loading}
              size="lg"
            >
              {loading
                ? copy.pages.resetPassword.sending
                : copy.pages.resetPassword.requestPasswordReset}
            </InputSubmit>
          </form>
          <BackToLoginComponent />
        </Form>
      )}

      {currentUsername && (
        <Form {...sendNewPasswordForm}>
          <form
            onSubmit={sendNewPasswordForm.handleSubmit(onSendNewPasswordSubmit)}
            className="flex flex-col gap-8"
            method="POST"
          >
            {error && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription className="text-lg ml-2">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <FormField
              control={sendNewPasswordForm.control}
              name="verificationCode"
              render={({ field }) => (
                <VerifyEmailInput
                  maxLength={6}
                  {...field}
                  label={copy.pages.resetPassword.verificationCode}
                  description={
                    copy.pages.resetPassword.verificationCodeDescription
                  }
                />
              )}
            />

            <FormField
              control={sendNewPasswordForm.control}
              name="newPassword"
              render={({ field }) => (
                <InputPassword
                  label={copy.common.form.newPassword}
                  placeholder={`${copy.common.form.your} ${copy.common.form.newPassword}`}
                  description={copy.common.form.passwordInputDescription}
                  autoComplete="current-password"
                  hasIcon
                  {...field}
                />
              )}
            />

            <input
              type="hidden"
              className="hidden"
              {...sendNewPasswordForm.register('username')}
              value={currentUsername || ''}
            />

            <InputSubmit
              hasIcon
              className="uppercase"
              isLoading={loading}
              disabled={loading}
              size="lg"
            >
              {loading
                ? copy.pages.resetPassword.sending
                : copy.pages.resetPassword.resetPasswordNow}
            </InputSubmit>
          </form>

          <BackToLoginComponent />
        </Form>
      )}
    </>
  );
}
